import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import { AuthProvider } from './lib/auth.js';
import { ApiError } from './lib/api.js';
import { endStaffSessionOn401 } from './lib/staff-session.js';
import { router } from './routes/index.js';
import { registerCounterServiceWorker } from './lib/service-worker.js';
import { LocaleProvider, storedLocale } from './lib/i18n.js';
import { ThemeProvider } from './lib/theme.js';
import '@signapass/ui/styles.css';

/**
 * TanStack Query for server state, no Redux — AGENTS.md §7.
 *
 * Defaults chosen for the counter rather than the dashboard: the staff app runs
 * on a phone with an intermittent connection, so a failed read retries, and a
 * *write* never does. Retrying a write client-side would issue a second stamp;
 * the idempotency key makes that safe on the server, but the retry belongs
 * where the key is generated, not in a global default.
 */
const onError = (error: unknown, queryKey?: readonly unknown[]) =>
  endStaffSessionOn401(queryClient)(error, queryKey);

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error, query) => onError(error, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (error) => onError(error) }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Not found, forbidden and unauthenticated are answers, not failures.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

/**
 * The locale to start in, before anything has loaded.
 *
 * The last choice made in this browser, falling back to English. Not the
 * browser's own `navigator.language`: DESIGN-SYSTEM.md §6 is about a bilingual
 * market where a merchant on an English laptop may well want Arabic, and
 * guessing from the device means overriding a deliberate choice every time they
 * use a different one. The server's copy on `User`/`StaffMember` replaces this
 * as soon as the session loads.
 */
const initialLocale = storedLocale() ?? 'en';

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <LocaleProvider initial={initialLocale}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </QueryClientProvider>
      </LocaleProvider>
    </ThemeProvider>
  </StrictMode>,
);

/**
 * The counter is the only installable surface (AGENTS.md §7), and this call is
 * a no-op anywhere else — the surface check is inside, next to the scope the
 * worker registers with.
 */
registerCounterServiceWorker();
